import cn from 'classnames';
import Link from 'next/link';
// Types
import {
  FeaturedStreamsType,
  getFeaturedStreamActionState,
} from '../FeaturedStreamsCarousel.helpers';
// UI
import Text from 'ui3/Text/Text';
// Components
import Button from 'ui3/Button/Button';
import LinkButton from 'ui3/LinkButton/LinkButton';
import MillionsIcon from 'ui3/MillionsIcon/MillionsIcon';
import SocialShareQRButton from 'components/common3/SocialShareQRButton/SocialShareQRButton';
// Helpers
import { formatStreamDate } from 'helpers/dateText';
import { parseForInnerHTML } from 'helpers/textEditor';
import { computeOrderStreamPath } from 'helpers/routes';
import { getStreamSocialShareText, getStreamerName } from 'helpers/streams';
// Hooks
import { useGetCurrUser, useStreamPurchaseNavigation } from 'hooks';
// Styles
import styles from './DesktopCarousel.module.scss';

interface DesktopCarouselProps {
  streams: FeaturedStreamsType[];
  currentSlide: number;
  onSlideChange: (slideIndex: number) => void;
  fullWidth?: boolean;
  withStoreChannelLink?: string;
  useArrowNavigation?: boolean;
  onPrev?: () => void;
  onNext?: () => void;
  isProfilePage?: boolean;
  showOfficialPpvTag?: boolean;
}

const DesktopCarousel = ({
  streams,
  currentSlide,
  fullWidth = false,
  withStoreChannelLink,
  onPrev,
  onNext,
  showOfficialPpvTag = true,
}: DesktopCarouselProps) => {
  const { data: userData } = useGetCurrUser();
  const { navigateToStream } = useStreamPurchaseNavigation();

  const currentStream = streams?.[currentSlide];

  if (!currentStream || !currentStream.stream) {
    return null;
  }

  const stream = currentStream.stream;
  const {
    buttonText,
    hasAccessToWatch,
    isUserStreamCreator,
    showOfficialPpvTagForSlide,
  } = getFeaturedStreamActionState(
    stream,
    userData?.me?.id,
    showOfficialPpvTag
  );

  const handleBuyButtonClick = () => {
    navigateToStream(currentStream.stream, hasAccessToWatch);
  };

  const hasMultipleStreams = streams.length > 1;

  // Parse description for ellipsis
  const descriptionPlainText = currentStream.stream.description
    ? parseForInnerHTML(currentStream.stream.description).replace(
        /<[^>]*>/g,
        ''
      )
    : '';

  // Social share text
  const streamerName = getStreamerName(currentStream.stream.store);
  const socialShareButtonText = getStreamSocialShareText(
    isUserStreamCreator,
    false,
    streamerName
  );

  return (
    <div
      className={cn(styles.desktopCarousel, {
        [styles.fullWidth]: fullWidth,
      })}
    >
      {withStoreChannelLink && (
        <div className={styles.buttonContainer}>
          <LinkButton
            link={withStoreChannelLink}
            variant="secondary"
            subVariant="text"
            iconSize={16}
            icon="chevronRight"
            iconPosition="right"
          >
            See All
          </LinkButton>
        </div>
      )}
      {/* Left Panel - Event Details */}
      <div className={styles.leftPanel}>
        <div className={styles.eventDetails}>
          <div className={styles.tagsRow}>
            {showOfficialPpvTagForSlide && (
              <div className={styles.officialPpvTag}>
                <Text variant="smallLabelBold" color="background-dim">
                  Official PPV
                </Text>
              </div>
            )}
            <div className={styles.dateTag}>
              <Text variant="smallLabelBold" color="background-dim">
                {formatStreamDate(currentStream.stream.scheduleDateUtc)}
              </Text>
            </div>
          </div>

          <Link
            href={computeOrderStreamPath(currentStream.stream)}
            prefetch={false}
          >
            <a className={styles.titleLink}>
              <Text variant="h4" className={styles.title}>
                {currentStream.stream.name}
              </Text>
            </a>
          </Link>

          {descriptionPlainText && (
            <div className={styles.description}>{descriptionPlainText}</div>
          )}
        </div>

        <div className={styles.actionButtons}>
          <Button
            variant="primary"
            size="large"
            className={styles.buyButton}
            onClick={handleBuyButtonClick}
          >
            {buttonText}
          </Button>
          <SocialShareQRButton
            onlyIcon
            itemTitle={currentStream.stream.name || ''}
            modalTitle="Share Stream"
            modalText="Share this stream in any way convenient for you"
            path={computeOrderStreamPath(currentStream.stream as any)}
            twitterText={socialShareButtonText}
          />
        </div>
      </div>

      {/* Arrow Navigation */}
      {hasMultipleStreams && (
        <>
          <button
            type="button"
            className={styles.arrowButton}
            onClick={onPrev}
            aria-label="Previous"
          >
            <MillionsIcon name="arrowLeft" className={styles.arrowIcon} />
          </button>
          <button
            type="button"
            className={styles.arrowButton}
            onClick={onNext}
            aria-label="Next"
          >
            <MillionsIcon name="arrowRight" className={styles.arrowIcon} />
          </button>
        </>
      )}

      {/* Right Panel - Single Image */}
      <div className={styles.rightPanel}>
        <div className={styles.imageContainer}>
          {currentStream.stream.imageURL && (
            <img
              src={currentStream.stream.imageURL}
              alt={currentStream.stream.name || 'Event poster'}
              className={styles.posterImage}
            />
          )}
          <div className={styles.imageShading} />
        </div>
      </div>
    </div>
  );
};

export default DesktopCarousel;
